/**
 * Modelo do TEMPLATE DE CERTIFICADO de apresentação (por evento).
 *
 * Documento JSON versionado, próprio do JEMS3 (não há formato de mercado p/
 * "certificado com campos dinâmicos" — ver discussão no editor). Coordenadas
 * SEMPRE no sistema do canvas A4 paisagem fixo 1123×794px (@96dpi) — o mesmo
 * da folha em tela e do PDF (conversão px→pt na geração).
 *
 * Camadas fixas: fundo (full-bleed, travado) → imagens → texto.
 */

/** Canvas A4 paisagem fixo @96dpi (297×210mm). Fonte única dessas medidas. */
export const CERTIFICATE_SHEET_W = 1123;
export const CERTIFICATE_SHEET_H = 794;

/**
 * Crop NÃO-DESTRUTIVO por moldura: a imagem original fica intacta; a moldura
 * (x/y/w/h do elemento) recorta via overflow, e o crop diz como a imagem se
 * encaixa dentro dela. `zoom` multiplica o tamanho "cover" da imagem;
 * offsets deslocam o centro (em px do canvas, pós-zoom).
 */
export interface CertificateCrop {
  zoom: number;      // >= 1 (1 = cover exato da moldura)
  offsetX: number;   // px do canvas
  offsetY: number;   // px do canvas
}

/**
 * Famílias "seguras": as ÚNICAS com todas as variações (regular/negrito/
 * itálico) nas fontes-padrão do PDF (pdf-lib StandardFonts) — WYSIWYG
 * tela↔PDF sem embutir arquivo de fonte.
 */
export type CertificateFontFamily = 'times' | 'helvetica' | 'courier';

/** Stack CSS equivalente de cada família (folha em tela = PDF). */
export const CERTIFICATE_FONT_CSS: { [f in CertificateFontFamily]: string } = {
  times: "Times, 'Times New Roman', serif",
  helvetica: 'Helvetica, Arial, sans-serif',
  courier: "'Courier New', Courier, monospace",
};

export interface CertificateTextElement {
  id: string;
  type: 'text';
  x: number;         // canto superior esquerdo da caixa
  y: number;
  w: number;         // largura da caixa (o texto quebra aqui; altura é do conteúdo)
  align: 'left' | 'center' | 'right';
  font: CertificateFontFamily;
  fontSize: number;  // px do canvas
  lineHeight: number; // multiplicador (ex.: 1.7)
  bold: boolean;
  italic: boolean;
  /** Texto com variáveis {{event}} {{title}} {{authors}} {{chairs}} {{date}}. */
  content: string;
}

export interface CertificateImageElement {
  id: string;
  type: 'image';
  x: number;
  y: number;
  w: number;
  h: number;
  imageId: number;   // CertificateImage no backend
  /** URL resolvida pelo backend (somente leitura; não persistir de volta). */
  url?: string;
  crop: CertificateCrop;
}

export type CertificateElement = CertificateTextElement | CertificateImageElement;

export interface CertificateBackground {
  imageId: number;
  url?: string;      // resolvida pelo backend (somente leitura)
  opacity: number;   // 0..1
}

/**
 * Regras de EMISSÃO (definidas pelo chair na aba "Regras de emissão"):
 * `allowedStatuses` = status de submissão que habilitam o autor a emitir.
 * SEMÂNTICA EXPLÍCITA: lista VAZIA = NINGUÉM emite (não é "todos"); bloco
 * ausente num JSON legado ganha o default ["ACCEPTED"] na sanitização.
 * Vivem no mesmo JSON do template → exportar/importar/copiar carregam a
 * política junto do desenho; na EXCLUSÃO do modelo elas ficam DORMENTES no
 * backend (content só com emission) e revivem no próximo "Criar do zero".
 * Aplicação é de UI (gate no menu + página do certificado; chair tem bypass)
 * — o PDF é gerado no cliente, não é enforcement de segurança.
 */
export interface CertificateEmissionRules {
  allowedStatuses: string[];   // valores de SubmissionStatus (uppercase)
}

export const DEFAULT_EMISSION_RULES: CertificateEmissionRules = { allowedStatuses: ['ACCEPTED'] };

export interface CertificateTemplate {
  version: 1;
  background: CertificateBackground | null;
  elements: CertificateElement[];
  emission?: CertificateEmissionRules | null;
}

/** true se o status da submissão está nas regras. FAIL-CLOSED: sem regras
 *  (defensivo — a sanitização sempre as põe) ou lista vazia → ninguém emite. */
export function certificateAllowedForStatus(template: CertificateTemplate | null, status: string): boolean {
  const allowed = template && template.emission && template.emission.allowedStatuses;
  if (!allowed || !allowed.length) { return false; }
  return allowed.includes(String(status || '').toUpperCase());
}

/** Normaliza um bloco emission cru (uppercase, só strings); null se inválido. */
export function sanitizeEmissionRules(raw: any): CertificateEmissionRules | null {
  if (!raw || !Array.isArray(raw.allowedStatuses)) { return null; }
  return {
    allowedStatuses: raw.allowedStatuses
      .filter((s: any) => typeof s === 'string' && s)
      .map((s: string) => s.toUpperCase()),
  };
}

/** Imagem enviada pelo chair (assinaturas, fundo, logos). */
export interface CertificateImage {
  id: number;
  name: string;
  url: string;
}

/**
 * Variáveis suportadas no texto (catálogo fixo, documentado no menu "{{" do
 * editor). O primeiro grupo vem de dados já carregados no fluxo do
 * certificado; sessão/sala, grupos e datas/local do evento são resolvidos
 * SOB DEMANDA (a página só busca quando o template os usa).
 */
export interface CertificateVars {
  event: string;           // nome do evento
  eventAcronym: string;    // sigla (ex.: "SBRC")
  eventYear: string;       // ano
  eventDates: string;      // período do evento (begin–end)
  eventLocation: string;   // cidade/país
  title: string;           // título do artigo
  id: string;              // número da submissão
  track: string;           // nome da trilha
  topics: string;          // tópicos da submissão (lista)
  submissionGroup: string; // grupo de submissões
  tpcGroup: string;        // grupo de TPC
  authors: string;         // autores com afiliações
  authorsNames: string;    // autores só nomes
  firstAuthor: string;     // primeiro autor (com afiliação)
  chairs: string;          // chairs do evento
  session: string;         // sessão do programa (título)
  sessionDate: string;     // data da sessão
  room: string;            // sala da sessão
  submittedAt: string;     // data de submissão
  date: string;            // data de emissão
}

export const CERTIFICATE_VAR_NAMES: Array<keyof CertificateVars> = [
  'event', 'eventAcronym', 'eventYear', 'eventDates', 'eventLocation',
  'title', 'id', 'track', 'topics', 'submissionGroup', 'tpcGroup',
  'authors', 'authorsNames', 'firstAuthor', 'chairs',
  'session', 'sessionDate', 'room', 'submittedAt', 'date',
];

/** Nomes de variável referenciados num template (p/ buscas sob demanda). */
export function collectUsedVars(template: CertificateTemplate): Set<string> {
  const used = new Set<string>();
  for (const el of template.elements) {
    if (!isTextElement(el)) { continue; }
    for (const token of (el.content || '').match(/\{\{\s*(\w+)\s*\}\}/g) || []) {
      used.add(token.replace(/[^\w]/g, ''));
    }
  }
  return used;
}

/** Substitui {{var}} pelos valores; variável desconhecida fica como está. */
export function resolveCertificateVars(content: string, vars: CertificateVars): string {
  return (content || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name] ?? '') : match);
}

/**
 * Dimensões intrínsecas de um SVG: width/height (aceita "px"/sem unidade);
 * sem eles, cai no viewBox. Null se indeterminável. Necessário porque SVG
 * só-viewBox reporta naturalWidth 0 em alguns browsers — e o crop por
 * moldura (tela e PDF) depende das dimensões.
 */
export function svgIntrinsicSize(svgText: string): { w: number, h: number } | null {
  try {
    const svg = new DOMParser().parseFromString(svgText, 'image/svg+xml').querySelector('svg');
    if (!svg) { return null; }
    const num = (v: string | null): number => {
      if (!v || /[%]/.test(v)) { return NaN; }
      const n = parseFloat(v);
      return Number.isFinite(n) && n > 0 ? n : NaN;
    };
    const w = num(svg.getAttribute('width'));
    const h = num(svg.getAttribute('height'));
    if (!isNaN(w) && !isNaN(h)) { return { w, h }; }
    const vb = (svg.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(parseFloat);
    if (vb.length === 4 && vb[2] > 0 && vb[3] > 0) { return { w: vb[2], h: vb[3] }; }
    return null;
  } catch {
    return null;
  }
}

/** Id local p/ elementos (só precisa ser único dentro do template). */
export function newCertificateElementId(): string {
  return 'el-' + Math.random().toString(36).slice(2, 10);
}

/**
 * Modelo NOVO = folha VAZIA (decisão de produto: não existe modelo-padrão
 * pré-preenchido; o enunciado hardcoded só vale p/ eventos SEM modelo).
 * Folha vazia salva é um template legítimo — distinto de "sem modelo"
 * (content null/{} no backend).
 */
export function emptyCertificateTemplate(): CertificateTemplate {
  // Default das regras: emitir apenas p/ ACEITAS (decisão de produto).
  return {
    version: 1, background: null, elements: [],
    emission: { allowedStatuses: [...DEFAULT_EMISSION_RULES.allowedStatuses] },
  };
}

/** Type guards. */
export function isTextElement(el: CertificateElement): el is CertificateTextElement {
  return el.type === 'text';
}
export function isImageElement(el: CertificateElement): el is CertificateImageElement {
  return el.type === 'image';
}

/**
 * Normaliza um template vindo do backend/importação: descarta o que não
 * reconhece e aplica defaults — nunca deixa um JSON malformado quebrar a
 * página do certificado do autor.
 */
export function sanitizeCertificateTemplate(raw: any): CertificateTemplate | null {
  if (!raw || typeof raw !== 'object' || !Array.isArray(raw.elements)) { return null; }
  const clampNum = (v: any, def: number, min: number, max: number) => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : def;
  };
  const elements: CertificateElement[] = [];
  for (const el of raw.elements) {
    if (!el || typeof el !== 'object') { continue; }
    const base = {
      id: typeof el.id === 'string' && el.id ? el.id : newCertificateElementId(),
      x: clampNum(el.x, 0, -CERTIFICATE_SHEET_W, CERTIFICATE_SHEET_W),
      y: clampNum(el.y, 0, -CERTIFICATE_SHEET_H, CERTIFICATE_SHEET_H),
    };
    if (el.type === 'text') {
      elements.push({
        ...base,
        type: 'text',
        w: clampNum(el.w, 720, 40, CERTIFICATE_SHEET_W),
        align: el.align === 'left' || el.align === 'right' ? el.align : 'center',
        font: el.font === 'helvetica' || el.font === 'courier' ? el.font : 'times',
        fontSize: clampNum(el.fontSize, 30, 8, 120),
        lineHeight: clampNum(el.lineHeight, 1.7, 1, 3),
        bold: !!el.bold,
        italic: !!el.italic,
        content: typeof el.content === 'string' ? el.content : '',
      });
    } else if (el.type === 'image' && Number.isFinite(Number(el.imageId))) {
      elements.push({
        ...base,
        type: 'image',
        w: clampNum(el.w, 200, 10, CERTIFICATE_SHEET_W),
        h: clampNum(el.h, 100, 10, CERTIFICATE_SHEET_H),
        imageId: Number(el.imageId),
        url: typeof el.url === 'string' ? el.url : undefined,
        crop: {
          zoom: clampNum(el.crop && el.crop.zoom, 1, 1, 10),
          offsetX: clampNum(el.crop && el.crop.offsetX, 0, -CERTIFICATE_SHEET_W, CERTIFICATE_SHEET_W),
          offsetY: clampNum(el.crop && el.crop.offsetY, 0, -CERTIFICATE_SHEET_H, CERTIFICATE_SHEET_H),
        },
      });
    }
  }
  // Folha vazia (elements: []) é VÁLIDA — só a AUSÊNCIA da estrutura
  // (content null/{}, sem array) significa "sem modelo".
  const bg = raw.background;
  const background: CertificateBackground | null =
    bg && typeof bg === 'object' && Number.isFinite(Number(bg.imageId))
      ? {
          imageId: Number(bg.imageId),
          url: typeof bg.url === 'string' ? bg.url : undefined,
          opacity: clampNum(bg.opacity, 1, 0, 1),
        }
      : null;
  // Regras de emissão: bloco presente é respeitado LITERALMENTE (vazio =
  // ninguém emite); JSON legado sem o bloco ganha o default ("Aceita").
  const emission: CertificateEmissionRules = sanitizeEmissionRules(raw.emission)
    || { allowedStatuses: [...DEFAULT_EMISSION_RULES.allowedStatuses] };
  return { version: 1, background, elements, emission };
}

/** Payload persistível: remove urls resolvidas (voláteis) antes do PUT/export. */
export function serializeCertificateTemplate(t: CertificateTemplate): CertificateTemplate {
  return {
    version: 1,
    background: t.background ? { imageId: t.background.imageId, opacity: t.background.opacity } : null,
    elements: t.elements.map(el => isImageElement(el)
      ? { id: el.id, type: 'image', x: el.x, y: el.y, w: el.w, h: el.h, imageId: el.imageId, crop: { ...el.crop } }
      : { ...el }),
    // SEMPRE persiste o bloco (lista vazia é estado válido: ninguém emite).
    emission: { allowedStatuses: t.emission ? [...t.emission.allowedStatuses] : [] },
  };
}
